<template>
  <div class="phase-ranking min-h-screen bg-neutral-50 py-6 px-4">
    <div class="max-w-3xl mx-auto">
      <h1 class="text-xl font-bold text-neutral-900">Ranking — {{ phaseName || "Fase" }}</h1>
      <p v-if="olympicTitle" class="text-sm text-neutral-600 mt-1 mb-5">{{ olympicTitle }}</p>

      <div v-if="loading" class="text-center py-10 text-neutral-500">Cargando ranking…</div>

      <div v-else-if="error" class="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900">
        {{ error }}
      </div>

      <div v-else-if="rows.length === 0" class="text-neutral-600">
        Aún no hay participantes en este ranking.
      </div>

      <div v-else class="overflow-x-auto rounded-xl border border-neutral-200 bg-white shadow-sm">
        <table class="w-full text-sm">
          <thead class="bg-neutral-100 text-neutral-700">
            <tr>
              <th class="text-left px-4 py-3 font-semibold">Puesto</th>
              <th class="text-left px-4 py-3 font-semibold">Usuario</th>
              <th class="text-left px-4 py-3 font-semibold">País</th>
              <th class="text-right px-4 py-3 font-semibold">Tiempo (s)</th>
              <th class="text-right px-4 py-3 font-semibold">Puntos</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in rows"
              :key="`${row.userId}-${row.position}`"
              :class="row.currentUser ? 'bg-amber-50' : 'border-t border-neutral-100'"
            >
              <td class="px-4 py-3 font-medium">{{ row.position }}</td>
              <td class="px-4 py-3">{{ row.publicAlias || "—" }}</td>
              <td class="px-4 py-3">{{ row.country || "—" }}</td>
              <td class="px-4 py-3 text-right tabular-nums">{{ formatTimeSeconds(row.timeSeconds) }}</td>
              <td class="px-4 py-3 text-right font-semibold">{{ formatPoints(row.pointsEarned) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <router-link :to="backLink" class="inline-block mt-6 text-sm font-semibold text-amber-800 hover:text-amber-900">
        ← Volver
      </router-link>
    </div>
  </div>
</template>

<script>
import { getPhaseRanking, getOlympicBySlug, getOlympicById } from "@/services/olympics.service";

export default {
  name: "OlimpiadasPhaseRanking",
  data() {
    return {
      loading: true,
      error: null,
      rows: [],
      phaseName: "",
      olympicTitle: "",
      olympicId: null,
      moduleId: null,
      slug: null,
    };
  },
  computed: {
    backLink() {
      if (this.moduleId) {
        const query = this.slug ? { slug: this.slug } : {};
        return { path: `/course/${this.moduleId}/phases`, query };
      }
      if (this.$route.params.slug) return "/olimpiadas";
      return "/dashboard";
    },
  },
  async mounted() {
    await this.load();
  },
  methods: {
    async resolveOlympicId() {
      const q = this.$route.query;
      if (q.olympicId) return Number(q.olympicId);

      if (this.$route.params.olympicId) return Number(this.$route.params.olympicId);

      const courseState = this.$store.state.course;
      const storeOlympic = courseState && courseState.olympic;
      const routeSlug = this.$route.params.slug;
      if (storeOlympic && storeOlympic.id && (!routeSlug || storeOlympic.slug === routeSlug)) {
        return Number(storeOlympic.id);
      }

      if (routeSlug) {
        const { data: olympic } = await getOlympicBySlug(routeSlug);
        return olympic && olympic.id ? Number(olympic.id) : null;
      }

      return null;
    },

    async loadOlympicMeta(olympicId) {
      const routeSlug = this.$route.params.slug;
      let olympic = null;
      if (routeSlug) {
        const { data } = await getOlympicBySlug(routeSlug);
        olympic = data;
      } else {
        const { data } = await getOlympicById(olympicId);
        olympic = data;
      }
      if (!olympic) return;

      this.olympicTitle = olympic.title || olympic.description || "";
      this.slug = olympic.slug || routeSlug || this.slug;
      this.moduleId = olympic.moduleId ? Number(olympic.moduleId) : this.moduleId;

      const phaseId = Number(this.$route.params.phaseId);
      const phases = olympic.phases || [];
      for (let i = 0; i < phases.length; i++) {
        if (Number(phases[i].id) === phaseId) {
          this.phaseName = phases[i].name;
          break;
        }
      }
    },

    formatPoints(value) {
      return value != null ? value : "—";
    },

    formatTimeSeconds(value) {
      if (value == null || value === "") return "—";
      const seconds = Number(value);
      if (Number.isNaN(seconds)) return "—";
      return `${seconds} s`;
    },

    normalizeRankingRows(data) {
      if (Array.isArray(data)) return data;
      if (data && Array.isArray(data.entries)) return data.entries;
      if (data && Array.isArray(data.ranking)) return data.ranking;
      return [];
    },

    async load() {
      this.loading = true;
      this.error = null;
      this.rows = [];

      const phaseId = Number(this.$route.params.phaseId);
      if (Number.isNaN(phaseId)) {
        this.error = "Fase no válida.";
        this.loading = false;
        return;
      }

      if (this.$route.query.moduleId) {
        this.moduleId = Number(this.$route.query.moduleId);
      }
      if (this.$route.query.slug) {
        this.slug = String(this.$route.query.slug);
      }

      let olympicId = null;
      try {
        olympicId = await this.resolveOlympicId();
      } catch (e) {
        console.error("Error resolving olympic id:", e);
        this.error = "No se pudo identificar la olimpiada.";
        this.loading = false;
        return;
      }

      if (!olympicId || Number.isNaN(olympicId)) {
        this.error = "No se pudo identificar la olimpiada.";
        this.loading = false;
        return;
      }

      this.olympicId = olympicId;
      this.loadOlympicMeta(olympicId).catch((e) => console.warn("Meta ranking:", e));

      try {
        const { data } = await getPhaseRanking(olympicId, phaseId);
        this.rows = this.normalizeRankingRows(data);
      } catch (e) {
        console.error("Error loading phase ranking:", e);
        const status = e.response && e.response.status;
        const msg = e.response && e.response.data && e.response.data.message;
        if (status === 403) {
          this.error = "El ranking de esta fase aún no ha sido publicado.";
        } else if (status === 404) {
          this.error = "No encontramos esta fase u olimpiada.";
        } else if (e.code === "ECONNABORTED") {
          this.error = "El ranking tardó demasiado en responder. Intenta de nuevo.";
        } else {
          this.error = msg || "No se pudo cargar el ranking. Intenta de nuevo más tarde.";
        }
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
